import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ComponentProps } from 'react';

import Image from '@vs-shop/image-link/Image';
import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { CarouselContent, CarouselItem } from '@vs-shop/ui/carousel';
import { clsxm } from '@vs-shop/utils';

/**
 * The product gallery: the photograph a shopper is looking at, the strip of
 * everything else the product has, and the marks that sit over the imagery.
 * Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <ProductGallery>
 *     <ProductGalleryViewport>
 *       <ProductGalleryStage className="hidden lap:block">
 *         <ProductGalleryImage variant={INLINE} src={src} alt={alt} width={924} height={693} preload />
 *         <ProductGalleryOpenButton aria-label={openLabel} onClick={…} />
 *       </ProductGalleryStage>
 *
 *       <ProductGalleryStrip className="lap:hidden">
 *         <Carousel setApi={setApi}>
 *           <ProductGalleryStripContent variant={INLINE}>
 *             {media.map((item, idx) => (
 *               <ProductGalleryStripItem key={idx}>…</ProductGalleryStripItem>
 *             ))}
 *           </ProductGalleryStripContent>
 *           <CarouselDots … />
 *         </Carousel>
 *       </ProductGalleryStrip>
 *
 *       <ProductGalleryPrevControl variant={INLINE} aria-label={prevLabel} onClick={…} />
 *       <ProductGalleryNextControl variant={INLINE} aria-label={nextLabel} onClick={…} />
 *
 *       <ProductGalleryDiscountBadge>{medallion}</ProductGalleryDiscountBadge>
 *       <ProductGalleryAgeBadge>{ageMark}</ProductGalleryAgeBadge>
 *       <ProductGalleryLabels><ProductLabelList>…</ProductLabelList></ProductGalleryLabels>
 *     </ProductGalleryViewport>
 *
 *     <ProductGalleryThumbBar variant={INLINE}>
 *       <ProductGalleryThumbGrid variant={INLINE}>
 *         <ProductGalleryThumb variant={INLINE} aria-current={…} onClick={…}>
 *           <ProductGalleryThumbImage variant={INLINE} src={thumbSrc} alt={alt} width={168} height={127} />
 *         </ProductGalleryThumb>
 *         <ProductGalleryThumbChip onClick={…}><Icon /><span>{copy}</span></ProductGalleryThumbChip>
 *       </ProductGalleryThumbGrid>
 *     </ProductGalleryThumbBar>
 *   </ProductGallery>
 *
 * The same parts inside the lightbox are `productGalleryLightbox.tsx`, whose
 * header carries that composition.
 *
 * **The gallery has two surroundings, and `variant` is which one a part paints
 * for.** `INLINE` is the gallery in the page — the card, its rail, its
 * hover-revealed controls. `OVERLAY` is the same section inside
 * {@link ProductGalleryLightbox}, where the imagery has the viewport to itself:
 * the media is capped against the viewport rather than filling a frame, the
 * rail stands as a column beside the stage, and the step controls no longer
 * wait for a hover that a touch device never delivers. Only the parts that
 * genuinely paint differently carry the axis; the rest carry none, because an
 * axis with one arm is an appearance decided by nothing.
 *
 * Which parts a surrounding has is still not an appearance. The lightbox has a
 * panel, a close control and a title the page has not; the page has a card the
 * lightbox has not. That is composition, and it is tenant JSX.
 *
 * **The single-frame stage and the swipe strip are two parts, not one part with
 * an appearance.** What differs between them is not how a box paints itself: the
 * stage holds ONE photograph, and the strip holds a slide per medium with a dot
 * row under it. That is which parts exist, which is tenant JSX by this
 * platform's rule — so a tenant showing one at each end of a breakpoint composes
 * both and hides one, and a tenant wanting the swipe strip at every width
 * composes only that. Neither part carries the breakpoint that chooses; where
 * the switch falls is layout between parts, and rcprofi's `lap` is rcprofi's.
 *
 * Two structures here are load-bearing and invisible when missing, so the parts
 * that own them render them rather than leaving them to a composition:
 * {@link ProductGalleryViewport} is both the positioning context every overlay
 * hangs in and the hover group the controls reveal themselves from, and
 * {@link ProductGalleryStripItem} carries the positioned frame each slide's
 * open control fills.
 *
 * Server-first: every part is a plain server component. The gallery's state —
 * which medium is selected, whether the lightbox is open — is the tenant's, held
 * in its own client composition, and reaches this markup as `aria-current` and
 * as handlers riding the native prop spread.
 */

/** The gallery's own surface: the card the viewport and the thumb rail sit on. */
const productGalleryClasses =
  'bg-background lap:m-0.5 lap:rounded-xl lap:ring lap:ring-foreground/10 overflow-hidden';

/**
 * The photograph itself: contained rather than cropped — a product shot is a
 * subject, and cropping it loses the subject. What differs between the two
 * surroundings is what bounds it: in the page it fills the frame it was given,
 * in the lightbox it is capped against the viewport and centres in what is
 * left.
 *
 * Each arm states its whole class list; there is no shared base. The two have
 * one utility in common, which is not enough shared truth to be worth stating
 * apart from the arms that use it.
 */
export const productGalleryImageVariants = cva('', {
  variants: {
    variant: {
      inline: 'bg-background h-full w-full object-contain',
      overlay: 'max-h-[80vh] max-w-full object-contain',
      unstyled: '',
    },
  },
});

type ProductGalleryVariantName = NonNullable<
  VariantProps<typeof productGalleryImageVariants>['variant']
>;

export type ProductGalleryVariant = ProductGalleryVariantName;

/**
 * `satisfies` binds these to the recipe above, so the two cannot drift. Every
 * part carrying the axis carries these same three arms.
 */
export const ProductGalleryVariant = {
  /** The gallery in the page. */
  INLINE: 'inline',
  /** The gallery filling the lightbox. */
  OVERLAY: 'overlay',
  /**
   * The opt-out: a tenant painting the part itself. An entry rather than an
   * omission, so what it paints — nothing — is stated in the list.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductGalleryVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductGalleryVariantProps = { readonly variant: ProductGalleryVariant };

export type ProductGalleryProps = ComponentProps<'div'>;

/** Root of the section. */
export const ProductGallery = ({ className, ...props }: ProductGalleryProps) => (
  <div data-slot="product-gallery" className={clsxm(productGalleryClasses, className)} {...props} />
);

/**
 * The box the imagery and everything over it share.
 *
 * `relative` and `group` are not decoration. Every overlay below positions
 * itself against this element, and the two step controls are `hidden` until this
 * box is hovered — a composition that dropped either would render controls that
 * can never appear and badges that escape to whatever ancestor happens to be
 * positioned, with no class differing anywhere.
 */
const productGalleryViewportClasses = 'group relative m-4 flex';

export type ProductGalleryViewportProps = ComponentProps<'div'>;

/** The imagery box, and the positioning context for the marks over it. */
export const ProductGalleryViewport = ({ className, ...props }: ProductGalleryViewportProps) => (
  <div
    data-slot="product-gallery-viewport"
    className={clsxm(productGalleryViewportClasses, className)}
    {...props}
  />
);

/**
 * The box a photograph's frame and its no-photograph stand-in share. Its aspect
 * ratio and height cap are what keep the page from reflowing as the shopper
 * steps through media of different shapes.
 *
 * Stated once rather than copied into both recipes, so the two cannot drift out
 * of the same box — which is the whole reason the empty state is a sibling part
 * rather than a prop on this one.
 */
const stageBox = 'flex aspect-[4/3] h-full max-h-[726px] w-full';

export type ProductGalleryStageProps = ComponentProps<'div'>;

/** The single-photograph frame. */
export const ProductGalleryStage = ({ className, ...props }: ProductGalleryStageProps) => (
  <div data-slot="product-gallery-stage" className={clsxm(stageBox, className)} {...props} />
);

/**
 * The same frame with no photograph in it — for a product whose imagery is
 * missing before anything is requested, which is a different state from a URL
 * that failed to load. What goes inside is the tenant's; this package ships only
 * the box, centred where the photograph would have been.
 *
 * In the lightbox the box is not the stage's: nothing else is on screen, so it
 * takes the height the imagery would have taken there.
 */
export const productGalleryPlaceholderVariants = cva('', {
  variants: {
    variant: {
      inline: `${stageBox} flex-col items-center justify-center`,
      overlay: 'flex h-[80vh] w-full items-center justify-center',
      unstyled: '',
    },
  },
});

export type ProductGalleryPlaceholderProps = ComponentProps<'div'> & ProductGalleryVariantProps;

/** The stage's box with no imagery in it. */
export const ProductGalleryPlaceholder = ({
  className,
  variant,
  ...props
}: ProductGalleryPlaceholderProps) => (
  <div
    data-slot="product-gallery-placeholder"
    className={clsxm(productGalleryPlaceholderVariants({ variant }), className)}
    {...props}
  />
);

/**
 * A gallery photograph.
 *
 * Renders the single-crop `Image` seam and implements no fallback flow of its
 * own: a host that aliases the specifier gets its resizing and its
 * backend-fallback retry, and one that does not resolves the portable plain
 * `<img>`. The gallery is not art-directed — one crop at every width — so there
 * is no `<picture>` and no `sources` here.
 *
 * `src` is final and renderable: every CDN/Liip `{filter}` resolution stays
 * app-side.
 */
export type ProductGalleryImageProps = VsImageProps & ProductGalleryVariantProps;

/** The photograph filling {@link ProductGalleryStage} or a strip slide. */
export const ProductGalleryImage = ({ className, variant, ...props }: ProductGalleryImageProps) => (
  <Image className={clsxm(productGalleryImageVariants({ variant }), className)} {...props} />
);

/**
 * The invisible control covering the imagery, which opens the lightbox.
 *
 * It fills the positioned box it is composed into — the stage against the
 * viewport, a slide against its own frame — so the whole photograph stays the
 * click target it is today. It is out of the tab order: the thumb rail already
 * reaches every medium with a real, named control, so a second tab stop over the
 * same photograph would announce nothing new.
 */
const productGalleryOpenButtonClasses = 'absolute inset-0 z-10 h-full w-full';

export type ProductGalleryOpenButtonProps = ComponentProps<'button'>;

/** The full-surface open control over a photograph. */
export const ProductGalleryOpenButton = ({
  className,
  tabIndex,
  ...props
}: ProductGalleryOpenButtonProps) => (
  <button
    data-slot="product-gallery-open-button"
    className={clsxm(productGalleryOpenButtonClasses, className)}
    tabIndex={tabIndex ?? -1}
    {...props}
  />
);

/** The box the swipeable strip of media sits in. */
const productGalleryStripClasses = 'flex w-full flex-col';

export type ProductGalleryStripProps = ComponentProps<'div'>;

/**
 * The swipe strip's box. What slides inside it is `@vs-shop/ui`'s carousel,
 * composed by the tenant, because whether the strip loops and what drives its
 * dots are the tenant's decisions rather than the gallery's.
 */
export const ProductGalleryStrip = ({ className, ...props }: ProductGalleryStripProps) => (
  <div
    data-slot="product-gallery-strip"
    className={clsxm(productGalleryStripClasses, className)}
    {...props}
  />
);

/**
 * The strip's track. Cancels the carousel's default inter-slide gutter — a
 * gallery's photographs butt against each other, so a partially-visible
 * neighbour reads as the next photograph rather than as a gap.
 *
 * In the page the track takes a fixed share of the viewport, so the copy below
 * the gallery keeps its place as the shopper swipes. In the lightbox there is
 * nothing below it, and the track takes what the panel gives it.
 */
export const productGalleryStripContentVariants = cva('', {
  variants: {
    variant: {
      inline: '-ml-0 h-[30vh]',
      overlay: '-ml-0',
      unstyled: '',
    },
  },
});

export type ProductGalleryStripContentProps = ComponentProps<typeof CarouselContent> &
  ProductGalleryVariantProps;

/** The strip's scrolling track. Keeps the primitive's own slot. */
export const ProductGalleryStripContent = ({
  className,
  variant,
  ...props
}: ProductGalleryStripContentProps) => (
  <CarouselContent
    className={clsxm(productGalleryStripContentVariants({ variant }), className)}
    {...props}
  />
);

/** One slide, sized to the whole strip so exactly one photograph is in view. */
const productGalleryStripItemClasses = 'w-full basis-auto pl-0';

/**
 * The positioned frame inside a slide. Load-bearing and invisible when missing:
 * the open control fills it, and without it that control would size itself
 * against the viewport and cover every slide at once.
 */
const productGalleryStripSlideClasses = 'relative h-full w-full';

export type ProductGalleryStripItemProps = ComponentProps<typeof CarouselItem>;

/** One slide of the strip, with the frame its media and control hang in. */
export const ProductGalleryStripItem = ({
  className,
  children,
  ...props
}: ProductGalleryStripItemProps) => (
  <CarouselItem className={clsxm(productGalleryStripItemClasses, className)} {...props}>
    <div data-slot="product-gallery-strip-slide" className={productGalleryStripSlideClasses}>
      {children}
    </div>
  </CarouselItem>
);

/**
 * The step controls, one per side.
 *
 * Two parts rather than one with a side prop: which direction a control steps is
 * what it IS, not how it paints. Each hangs half off the imagery and appears
 * only while the viewport is hovered, which is why it is `hidden` until
 * {@link ProductGalleryViewport}'s group says otherwise — and why the chevron
 * inside carries an offset, so the visible half of a circle that is mostly
 * off-frame still has its mark near the middle of what a reader can see.
 */
/** Everything the two sides share, stated once so they cannot drift apart. */
const controlBox =
  'absolute top-1/2 z-10 size-[90px] -translate-y-1/2 ' +
  'bg-background ring-foreground/20 items-center justify-center rounded-full ring ' +
  'disabled:cursor-not-allowed ' +
  'lap:size-[120px]';

/**
 * When a control is on screen, which is the axis the two surroundings differ on.
 *
 * In the page a control waits for the viewport to be hovered: it hangs half off
 * the imagery, and showing it unasked would put a disc over the photograph on
 * every card-sized gallery. In the lightbox the imagery has the screen to
 * itself, there is nothing to keep clear of, and a control that waits for a
 * hover is a control a touch device never sees.
 */
const controlPresence = {
  inline: 'lap:group-hover:flex hidden',
  overlay: 'flex',
  unstyled: '',
} as const satisfies Record<ProductGalleryVariant, string>;

export const productGalleryPrevControlVariants = cva(
  `${controlBox} lap:-left-[60px] -left-[40px]`,
  {
    variants: { variant: controlPresence },
  }
);

export const productGalleryNextControlVariants = cva(
  `${controlBox} lap:-right-[60px] -right-[40px]`,
  { variants: { variant: controlPresence } }
);

/**
 * The chevron's offset inside each control, mirrored per side. Internal
 * anatomy: the control renders its own chevron, so no composition can reach the
 * element these paint and neither is exported from the package.
 */
const controlIcon = 'lap:size-9 relative size-6 shrink-0';

const productGalleryPrevControlIconClasses = `${controlIcon} lap:left-4 left-3`;

const productGalleryNextControlIconClasses = `${controlIcon} lap:right-4 right-3`;

/**
 * A control is named by the `aria-label` its tenant passes; its children are
 * whatever else that tenant wants announced. The chevron is the part's own, so
 * the two sides cannot drift apart.
 *
 * `variant` and `size` are the part's own answers, not a caller's: a step
 * control is the same shape in every tenant, and letting the primitive's axes
 * through would be the section painting itself only until someone passed one.
 * `className` remains the override, as on every other part.
 */
export type ProductGalleryControlProps = Omit<ButtonProps, 'variant' | 'size'> &
  ProductGalleryVariantProps;

/** The step to the previous medium. */
export const ProductGalleryPrevControl = ({
  className,
  children,
  variant,
  ...props
}: ProductGalleryControlProps) => (
  <Button
    variant="secondary"
    size="icon"
    className={clsxm(productGalleryPrevControlVariants({ variant }), className)}
    {...props}
  >
    <ChevronLeft
      data-slot="product-gallery-prev-control-icon"
      className={productGalleryPrevControlIconClasses}
    />
    {children}
  </Button>
);

/** The step to the next medium. */
export const ProductGalleryNextControl = ({
  className,
  children,
  variant,
  ...props
}: ProductGalleryControlProps) => (
  <Button
    variant="secondary"
    size="icon"
    className={clsxm(productGalleryNextControlVariants({ variant }), className)}
    {...props}
  >
    <ChevronRight
      data-slot="product-gallery-next-control-icon"
      className={productGalleryNextControlIconClasses}
    />
    {children}
  </Button>
);

/**
 * The discount medallion at the imagery's trailing top corner: where it hangs
 * AND how large it reads.
 *
 * The size belongs here rather than on the tenant's artwork, because how big a
 * medallion reads against a product photograph is the section's judgement — the
 * same answer the product card gives on its own scale. A tenant supplies the
 * mark and it fills this box.
 */
const productGalleryDiscountBadgeClasses =
  'lap:size-[114px] lap:text-lg absolute top-0 -right-0 z-10 size-[86px]';

export type ProductGalleryDiscountBadgeProps = ComponentProps<'div'>;

/** Where the discount medallion hangs. What fills it is the tenant's mark. */
export const ProductGalleryDiscountBadge = ({
  className,
  ...props
}: ProductGalleryDiscountBadgeProps) => (
  <div
    data-slot="product-gallery-discount-badge"
    className={clsxm(productGalleryDiscountBadgeClasses, className)}
    {...props}
  />
);

/**
 * The age-restriction mark, at the imagery's trailing bottom corner. Carries the
 * gallery's own background behind it rather than relying on the photograph, so
 * it stays legible over any imagery.
 */
const productGalleryAgeBadgeClasses = 'bg-background absolute right-4 bottom-0 z-10 rounded-full';

export type ProductGalleryAgeBadgeProps = ComponentProps<'div'>;

/** Where the age-restriction mark hangs. */
export const ProductGalleryAgeBadge = ({ className, ...props }: ProductGalleryAgeBadgeProps) => (
  <div
    data-slot="product-gallery-age-badge"
    className={clsxm(productGalleryAgeBadgeClasses, className)}
    {...props}
  />
);

/** The promotional chips, at the imagery's leading top corner. */
const productGalleryLabelsClasses = 'absolute top-0 left-0 z-10';

export type ProductGalleryLabelsProps = ComponentProps<'div'>;

/**
 * Where the promotional chips hang. The chips themselves are
 * `@vs-shop/commerce-ui/productLabel`, shared with the product card so the same
 * label reads as the same object in a listing and on the product's own page.
 */
export const ProductGalleryLabels = ({ className, ...props }: ProductGalleryLabelsProps) => (
  <div
    data-slot="product-gallery-labels"
    className={clsxm(productGalleryLabelsClasses, className)}
    {...props}
  />
);

/**
 * The band the thumbs live in.
 *
 * In the page it is a rail under the imagery. In the lightbox it is a column
 * beside the stage — a scrolling third of the height on a phone, the panel's
 * trailing edge above `lap`, with its own ground and a rule against the stage.
 */
export const productGalleryThumbBarVariants = cva('', {
  variants: {
    variant: {
      inline: 'bg-background lap:block hidden p-8',
      overlay:
        'bg-background-intense border-foreground/10 flex max-h-1/3 flex-col gap-6 ' +
        'overflow-y-auto rounded-r-lg border-l px-6 py-8 ' +
        'lap:max-h-full lap:max-w-[280px] des:max-w-[380px]',
      unstyled: '',
    },
  },
});

export type ProductGalleryThumbBarProps = ComponentProps<'div'> & ProductGalleryVariantProps;

/**
 * The band the thumbs are placed in. What it holds is composition — the page
 * gives it a grid and nothing else, the lightbox names the product above that
 * grid — which is why the grid below is a part rather than this one's internal
 * anatomy.
 */
export const ProductGalleryThumbBar = ({
  className,
  variant,
  ...props
}: ProductGalleryThumbBarProps) => (
  <div
    data-slot="product-gallery-thumb-bar"
    className={clsxm(productGalleryThumbBarVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The grid the thumbs are placed in. Its own part rather than the band's inner
 * element, because the lightbox puts the product's name above it inside the same
 * band — an element a composition has to reach around is an element it has to be
 * able to name.
 *
 * The column is denser than the rail at every width: it is narrower, and a thumb
 * there stands beside the photograph it selects rather than under it.
 */
export const productGalleryThumbGridVariants = cva('', {
  variants: {
    variant: {
      inline: 'lap:grid-cols-5 grid grid-cols-3 gap-4',
      overlay: 'lap:grid-cols-3 des:grid-cols-4 grid grid-cols-4 gap-2',
      unstyled: '',
    },
  },
});

export type ProductGalleryThumbGridProps = ComponentProps<'div'> & ProductGalleryVariantProps;

/** The grid a rail's or a column's thumbs sit in. */
export const ProductGalleryThumbGrid = ({
  className,
  variant,
  ...props
}: ProductGalleryThumbGridProps) => (
  <div
    data-slot="product-gallery-thumb-grid"
    className={clsxm(productGalleryThumbGridVariants({ variant }), className)}
    {...props}
  />
);

/**
 * One thumbnail in the rail.
 *
 * **Which thumb is the current one arrives as `aria-current` and as nothing
 * else.** It is already the attribute that names the current item in a set, it
 * is what a screen reader announces, and it rides the native prop spread — so
 * the part takes no `selected` prop that could disagree with it, and the current
 * thumb's ring is selected from the DOM rather than from a second source of
 * truth. Same painted result, one statement of it.
 */
export const productGalleryThumbVariants = cva(
  'bg-background aria-[current=true]:ring-primary aspect-[4/3] ring aria-[current=true]:ring-2',
  {
    variants: {
      variant: {
        inline: 'ring-foreground/20 hover:ring-primary shrink-0 overflow-hidden rounded-lg',
        overlay: 'ring-foreground/10 rounded-md',
        unstyled: '',
      },
    },
  }
);

export type ProductGalleryThumbProps = ComponentProps<'button'> & ProductGalleryVariantProps;

/** One thumbnail button. */
export const ProductGalleryThumb = ({ className, variant, ...props }: ProductGalleryThumbProps) => (
  <button
    data-slot="product-gallery-thumb"
    className={clsxm(productGalleryThumbVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The thumbnail's photograph — contained, never cropped, and rounded with the
 * button it fills, which is the one thing that differs between the two.
 */
export const productGalleryThumbImageVariants = cva('h-full w-full object-contain', {
  variants: {
    variant: {
      inline: 'rounded-lg',
      overlay: 'rounded-md',
      unstyled: '',
    },
  },
});

/** A thumbnail photograph. The same seam and the same rules as {@link ProductGalleryImage}. */
export type ProductGalleryThumbImageProps = VsImageProps & ProductGalleryVariantProps;

/** The photograph filling a thumbnail. */
export const ProductGalleryThumbImage = ({
  className,
  variant,
  ...props
}: ProductGalleryThumbImageProps) => (
  <Image className={clsxm(productGalleryThumbImageVariants({ variant }), className)} {...props} />
);

/**
 * The frame inside a thumbnail that a mark hangs in.
 *
 * Load-bearing and invisible when missing, which is why it is the thumb's
 * anatomy and not a composition's: {@link ProductGalleryThumbMark} positions
 * itself against this box, and without it the mark would centre on whichever
 * ancestor happens to be positioned — the whole rail, in a rail of thumbs.
 */
const productGalleryThumbFrameClasses = 'relative aspect-[4/3] rounded-md';

export type ProductGalleryThumbFrameProps = ComponentProps<'div'>;

/** The positioned frame a marked thumbnail's imagery and mark share. */
export const ProductGalleryThumbFrame = ({
  className,
  ...props
}: ProductGalleryThumbFrameProps) => (
  <div
    data-slot="product-gallery-thumb-frame"
    className={clsxm(productGalleryThumbFrameClasses, className)}
    {...props}
  />
);

/**
 * The mark centred over a thumbnail — a play badge on a video's still.
 *
 * How large it reads is the section's judgement about the thumbnail, the same
 * answer {@link ProductGalleryDiscountBadge} gives about the photograph, so the
 * tenant supplies artwork with no size of its own and it fills this box.
 */
const productGalleryThumbMarkClasses = 'absolute inset-1/2 size-10 -translate-1/2';

export type ProductGalleryThumbMarkProps = ComponentProps<'div'>;

/** Where a thumbnail's mark hangs. What fills it is the tenant's artwork. */
export const ProductGalleryThumbMark = ({ className, ...props }: ProductGalleryThumbMarkProps) => (
  <div
    data-slot="product-gallery-thumb-mark"
    className={clsxm(productGalleryThumbMarkClasses, className)}
    {...props}
  />
);

/**
 * A chip occupying a thumbnail's cell but standing for a set rather than one
 * photograph — the photographs the rail had no room for, the videos it does not
 * show. One part for both, because they are the same object: a cell with a mark
 * over a count. What the mark and the count are is the tenant's.
 */
const productGalleryThumbChipClasses =
  'flex aspect-[4/3] shrink-0 flex-col items-center justify-center gap-2 ' +
  'bg-background ring-foreground/20 hover:ring-primary overflow-hidden rounded-lg p-2 ring';

export type ProductGalleryThumbChipProps = ComponentProps<'button'>;

/** The "more photographs" / "N videos" cell. */
export const ProductGalleryThumbChip = ({ className, ...props }: ProductGalleryThumbChipProps) => (
  <button
    data-slot="product-gallery-thumb-chip"
    className={clsxm(productGalleryThumbChipClasses, className)}
    {...props}
  />
);
